import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, Eye, Save, Check, X } from 'lucide-react';
import { validate } from '../../validation/engine';
import {
    controlTestProgramService,
    TestProgramDetail,
    ProgramControlRow,
    ProgramImplementationRow,
} from '../../services/controlTestingService';
import {
    controlDeficiencyService,
    ControlDeficiency,
    DeficiencySeverity,
    DEFICIENCY_SEVERITIES,
} from '../../services/controlDeficiencyService';
import Modal from '../common/Modal';
import InlineIssueMeta from '../common/InlineIssueMeta';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import extractApiError from '../../utils/apiError';
import {
    SeamlessSurface,
    SeamlessCard,
    TypeChip,
    AssessmentPanel,
    AssessRow,
    NetScore,
    SectionLabel,
    ImplList,
    ResponseBar,
    SeamlessSkeleton,
    scoreBand,
} from '../audits/procedure-responses/seamless/SeamlessKit';

// Match RAT Programs (RiskPrograms.tsx) — surface only In-Progress / Submitted
// / Closed. CAT's DB uses Not-Started, Complete (maker submitted) and Approved
// (checker signed off), so map those onto the RAT vocabulary.
const STATUS_LABELS: Record<string, string> = {
    'Not-Started': 'In-Progress',
    Complete: 'Submitted',
    Approved: 'Closed',
};
const statusLabel = (status?: string | null) =>
    STATUS_LABELS[status || ''] || status || 'In-Progress';

const statusBadgeClass = (status?: string | null) => {
    switch (statusLabel(status)) {
        case 'Closed':      return 'bg-green-100 text-green-700';
        case 'Submitted':   return 'bg-purple-100 text-purple-700';
        case 'In-Progress': return 'bg-blue-100 text-blue-700';
        default:            return 'bg-gray-100 text-gray-600';
    }
};

interface TestProgramExecutionModalProps {
    programId: number;
    onClose: () => void;
    onSaved?: () => void;
    readOnly?: boolean;
    /** Checker opens it to approve: inputs are locked, footer shows Approve. */
    isChecker?: boolean;
}

/** RemarksModal — per-row observation editor as a popup (parity with the audit
 *  ControlTest body). Local draft, commit once on Done. */
const RemarksModal: React.FC<{
    heading: string;
    title: string;
    initialValue: string;
    readOnly: boolean;
    placeholder?: string;
    testId?: string;
    onCommit: (value: string) => void;
    onClose: () => void;
}> = ({ heading, title, initialValue, readOnly, placeholder, testId, onCommit, onClose }) => {
    const [draft, setDraft] = useState(initialValue);
    return (
        <Modal isOpen onClose={onClose} size="md">
            <Modal.Header onClose={onClose}>{heading}</Modal.Header>
            <Modal.Body>
                <p className="response-remarks-question">{title}</p>
                <FormControl.Textarea
                    label=""
                    data-testid={testId}
                    value={draft}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
                    rows={5}
                    placeholder={placeholder}
                    autoFocus
                    disabled={readOnly}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="fill" onClick={() => { onCommit(draft); onClose(); }} disabled={readOnly}>Done</Button>
            </Modal.Footer>
        </Modal>
    );
};

const TestProgramExecutionModal: React.FC<TestProgramExecutionModalProps> = ({
    programId,
    onClose,
    onSaved,
    readOnly = false,
    isChecker = false,
}) => {
    const { formatDate } = useEntitySettings();
    const [detail, setDetail] = useState<TestProgramDetail | null>(null);
    const [loading, setLoading] = useState(true);
    // setSavingId / setSavingScoreId gate the in-flight impl + score saves; the
    // values themselves aren't read (the kit owns row rendering), only the setters.
    const [, setSavingId] = useState<number | null>(null);
    const [, setSavingScoreId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    // Deficiencies (filtered for this program: source=testing, source_ref_id=programId)
    const [deficiencies, setDeficiencies] = useState<ControlDeficiency[]>([]);
    const [raiseFor, setRaiseFor] = useState<ProgramControlRow | null>(null);
    // Checker reject flow — project Modal (not window.prompt); reason sent to maker.
    const [showReject, setShowReject] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectErr, setRejectErr] = useState<string | null>(null);
    // Per-row observations (test_remarks → 110) format errors, keyed by impl id.
    // Mirrors the procedure-response obsErrors idiom: cleared on edit, set on
    // blur, and the row's save is blocked while an error is present.
    const [obsErrors, setObsErrors] = useState<Record<number, string>>({});
    // Observation note popup (parity with the audit ControlTest body).
    const [remarks, setRemarks] = useState<
        { heading: string; title: string; value: string; placeholder?: string; testId?: string; onCommit: (v: string) => void } | null
    >(null);
    // Cards are always open (no expand/collapse) — flat single-enclosure design.

    // Checker (and pure viewers) may not mutate scores/results — only approve.
    // Once the maker submits (Complete) or it's Approved, the maker's inputs lock
    // too — a submitted assessment is read-only until a checker rejects it back.
    const submittedOrApproved = detail?.status === 'Complete' || detail?.status === 'Approved';
    const writeBlocked = readOnly || isChecker || submittedOrApproved;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await controlTestProgramService.get(programId);
            setDetail(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load program') });
        } finally {
            setLoading(false);
        }
    }, [programId]);

    // One batched call for all deficiencies raised against this test program.
    // Replaces the previous N+1 (one call per control). Filtering happens on
    // the entity-wide /deficiencies endpoint via source + source_ref_id.
    const loadDeficiencies = useCallback(async () => {
        try {
            const { data } = await controlDeficiencyService.listAll({
                source: 'testing',
                limit: 200,
            });
            // Server doesn't index source_ref_id alone; filter client-side to be safe.
            setDeficiencies(data.filter(d => d.source_ref_id === programId));
        } catch (e: any) {
            // Soft-fail: deficiencies are auxiliary; don't block the runner UI.
            // eslint-disable-next-line no-console
            console.warn('Failed to load deficiencies', e);
        }
    }, [programId]);

    useEffect(() => { load(); }, [load]);
    // Run ONCE per program. Previously this effect depended on detail.controls,
    // which re-fired on every setDetail (score click), triggering a full
    // deficiency refetch each time — the main lag source.
    useEffect(() => { loadDeficiencies(); }, [loadDeficiencies]);

    const updateImpl = async (
        impl: ProgramImplementationRow,
        patch: { result?: 'Pass' | 'Fail' | 'NA'; remarks?: string },
    ) => {
        if (writeBlocked) return;
        // Optimistic local update so the pill flips immediately. Rollback on error.
        setDetail(prev => prev ? {
            ...prev,
            implementations: prev.implementations.map(r =>
                r.impl_score_id === impl.impl_score_id ? { ...r, ...patch } : r),
        } : prev);
        setSavingId(impl.impl_score_id);
        try {
            await controlTestProgramService.updateImplScore(programId, impl.impl_score_id, patch);
        } catch (e: any) {
            setDetail(prev => prev ? {
                ...prev,
                implementations: prev.implementations.map(r =>
                    r.impl_score_id === impl.impl_score_id ? impl : r),
            } : prev);
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save') });
        } finally {
            setSavingId(null);
        }
    };

    const updateScore = async (
        ctrl: ProgramControlRow,
        patch: { effectiveness_score?: number | null; existence_score?: number | null; test_remarks?: string },
    ) => {
        if (writeBlocked) return;
        // Optimistic: bump the local control row first so NetScoreBadge updates
        // without waiting on the network roundtrip.
        setDetail(prev => prev ? {
            ...prev,
            controls: prev.controls.map(c =>
                c.score_id === ctrl.score_id ? { ...c, ...patch } : c),
        } : prev);
        setSavingScoreId(ctrl.score_id);
        try {
            await controlTestProgramService.updateControlScore(programId, ctrl.score_id, patch);
        } catch (e: any) {
            setDetail(prev => prev ? {
                ...prev,
                controls: prev.controls.map(c =>
                    c.score_id === ctrl.score_id ? ctrl : c),
            } : prev);
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save score') });
        } finally {
            setSavingScoreId(null);
        }
    };

    const handleSubmit = async () => {
        if (writeBlocked) return;
        setSubmitting(true);
        try {
            await controlTestProgramService.submit(programId);
            setToast({ type: 'success', message: 'Program submitted for approval.' });
            onSaved?.();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to submit') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleApprove = async () => {
        if (!isChecker) return;
        setSubmitting(true);
        try {
            await controlTestProgramService.approve(programId);
            setToast({ type: 'success', message: 'Program approved — scores published to the control register.' });
            onSaved?.();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to approve') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!isChecker) return;
        const reason = rejectReason.trim();
        if (!reason) { setRejectErr('A reason is required to reject.'); return; }
        setSubmitting(true);
        try {
            await controlTestProgramService.reject(programId, reason);
            setToast({ type: 'success', message: 'Program rejected — returned to maker.' });
            setShowReject(false);
            onSaved?.();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to reject') });
        } finally {
            setSubmitting(false);
        }
    };

    // Regroup implementations under their control once per detail change, not
    // on every keystroke/slider re-render.
    const groupedByControl = useMemo(() => {
        const grouped: Record<number, { control: ProgramControlRow; impls: ProgramImplementationRow[] }> = {};
        if (detail) {
            for (const c of detail.controls) {
                grouped[c.control_id] = { control: c, impls: [] };
            }
            for (const i of detail.implementations) {
                if (!grouped[i.control_id]) {
                    grouped[i.control_id] = {
                        control: {
                            score_id: 0,
                            control_id: i.control_id,
                            control_code: '?',
                            control_name: '?',
                        } as ProgramControlRow,
                        impls: [],
                    };
                }
                grouped[i.control_id].impls.push(i);
            }
        }
        return grouped;
    }, [detail]);
    const totalImpls = detail?.implementations.length ?? 0;
    const completedImpls = detail?.implementations.filter(i => i.result).length ?? 0;
    const totalCtrls = detail?.controls.length ?? 0;
    // A score of ZERO is a score (V2). This tested truthiness, so a control
    // scored 0 on either dimension read as unscored and Submit never enabled —
    // while the server, which tests for NULL, would have accepted it. The
    // browser was stricter than the server and a legitimately complete
    // programme could not be submitted through the interface.
    const scoredCtrls = detail?.controls.filter(
        c => c.effectiveness_score != null && c.existence_score != null,
    ).length ?? 0;
    // Implementations are optional — a control may have none. Only require that
    // every implementation that DOES exist is completed; don't block submit on a
    // procedure that simply has zero impls. Controls must still all be scored.
    const allDone = completedImpls === totalImpls && totalCtrls > 0 && scoredCtrls === totalCtrls;

    // Match the staff procedure header format: Procedure: "<Name>" (<Type>).
    const headerTitle = detail
        ? <>Procedure: "<span className="item-highlight">{detail.test_name}</span>" (Control Test)</>
        : 'Loading program…';

    return (
        <Modal isOpen onClose={onClose} size="xl" closeOnOverlayClick={false}>
            <Modal.Header>
                {headerTitle}
                {readOnly && (
                    <span className="status-badge sm neutral" style={{ marginLeft: 8 }}>
                        <Eye size={12} /> Read-only
                    </span>
                )}
                {!readOnly && isChecker && (
                    <span className="status-badge sm warning" style={{ marginLeft: 8 }}>
                        <Check size={12} /> Checker
                    </span>
                )}
            </Modal.Header>
            <Modal.Body>
                {loading && <SeamlessSkeleton />}
                {!loading && detail && (
                    <div className="collapse-section-stack">
                        {/* Staff-style context row (label: value), not chip pills. */}
                        <div className="response-shell-context">
                            {detail.template_code && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Template: </span>
                                    <span className="response-shell-meta-value">{detail.template_code}</span>
                                </span>
                            )}
                            {detail.location_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Location: </span>
                                    <span className="response-shell-meta-value">{detail.location_name}</span>
                                </span>
                            )}
                            {detail.maker_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Maker: </span>
                                    <span className="response-shell-meta-value">{detail.maker_name}</span>
                                </span>
                            )}
                            {detail.checker_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Checker: </span>
                                    <span className="response-shell-meta-value">{detail.checker_name}</span>
                                </span>
                            )}
                            {(detail.start_date || detail.end_date) && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Period: </span>
                                    <span className="response-shell-meta-value">{formatDate(detail.start_date)} → {formatDate(detail.end_date)}</span>
                                </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(detail.status)}`}>
                                {statusLabel(detail.status)}
                            </span>
                            <span className="response-shell-meta-item" style={{ marginLeft: 'auto' }}>
                                <span className="response-shell-meta-value">{completedImpls} / {totalImpls} impls · {scoredCtrls} / {totalCtrls} scored</span>
                            </span>
                        </div>

                        {/* Checker's reject note — shown to the maker after a bounce-back
                            so they know what to fix. Hidden once re-submitted/approved. */}
                        {!isChecker && detail.rejection_reason && detail.status !== 'Complete' && detail.status !== 'Approved' && (
                            <div className="response-inline-warning" style={{ margin: '0 0 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-testid="BANNER_cat_reject_note">
                                <X size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                <span><strong>Rejected by checker:</strong> {detail.rejection_reason}</span>
                            </div>
                        )}

                        <div className="seamless-tight">
                        <SeamlessSurface>
                            {Object.values(groupedByControl).map(({ control, impls }, idx) => {
                                const controlDefs = deficiencies.filter(d => d.control_id === control.control_id);
                                const exi = control.existence_score ?? null;
                                const eff = control.effectiveness_score ?? null;
                                const net = (exi != null && eff != null) ? (exi as number) * (eff as number) : null;
                                const variant = scoreBand(net);
                                return (
                                    <SeamlessCard
                                        key={control.control_id}
                                        sno={idx + 1}
                                        snoLabel={`C${idx + 1}`}
                                        variant="control"
                                        title={control.control_name}
                                        typeChip={<TypeChip label={control.control_type} />}
                                        inlineRemark={
                                            <InlineIssueMeta
                                                label="Deficiencies"
                                                issues={controlDefs}
                                                testIdPrefix={`CHIP_ctp_deficiency_${control.control_id}`}
                                            />
                                        }
                                        assessment={
                                            <AssessmentPanel
                                                verdict={
                                                    <NetScore
                                                        text={net == null ? 'Not scored' : `${net}/25`}
                                                        variant={variant}
                                                        title="Net Score = Existence × Effectiveness (out of 25)"
                                                    />
                                                }
                                            >
                                                <AssessRow
                                                    label="Ex"
                                                    labelTitle="Existence"
                                                    value={control.existence_score ?? ''}
                                                    disabled={writeBlocked}
                                                    onChange={(v) => updateScore(control, { existence_score: v === '' ? null : (v as number) })}
                                                    testId={`SEL_ctp_existence_${control.control_id}`}
                                                />
                                                <AssessRow
                                                    label="Ef"
                                                    labelTitle="Effectiveness"
                                                    value={control.effectiveness_score ?? ''}
                                                    disabled={writeBlocked}
                                                    onChange={(v) => updateScore(control, { effectiveness_score: v === '' ? null : (v as number) })}
                                                    testId={`SEL_ctp_effectiveness_${control.control_id}`}
                                                />
                                            </AssessmentPanel>
                                        }
                                        impl={
                                            impls.length > 0 ? (
                                                <>
                                                    <SectionLabel>Implementations</SectionLabel>
                                                    <ImplList
                                                        items={impls.map(i => ({
                                                            id: i.impl_score_id,
                                                            sno: i.sequence_number,
                                                            title: i.target_label || '—',
                                                            sub: [i.target_subtitle, i.explanation].filter(Boolean).join(' · ') || null,
                                                            result: i.result || '',
                                                            observations: i.remarks || '',
                                                        }))}
                                                        disabled={readOnly}
                                                        testIdPrefix="ctp_impl"
                                                        onOpenObservation={(id) => {
                                                            const impl = impls.find(x => x.impl_score_id === id);
                                                            setRemarks({
                                                                heading: 'Observation',
                                                                title: impl?.target_label || 'Implementation',
                                                                value: impl?.remarks || '',
                                                                placeholder: 'Observation (optional)',
                                                                testId: `INP_ctp_impl_obs_${id}`,
                                                                onCommit: (v) => {
                                                                    setObsErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
                                                                    if (v && v.trim()) {
                                                                        const res = validate(110, v);
                                                                        if (!res.valid) { setObsErrors(prev => ({ ...prev, [id]: res.message ?? 'Invalid observations.' })); }
                                                                    }
                                                                    const im = impls.find(x => x.impl_score_id === id);
                                                                    if (im) updateImpl(im, { remarks: v });
                                                                },
                                                            });
                                                        }}
                                                        onResult={(id, result) => {
                                                            const impl = impls.find(x => x.impl_score_id === id);
                                                            if (impl) updateImpl(impl, { result: result as 'Pass' | 'Fail' | 'NA' });
                                                        }}
                                                        onObservations={(id, text) => {
                                                            setObsErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
                                                            setDetail(prev => prev ? {
                                                                ...prev,
                                                                implementations: prev.implementations.map(x =>
                                                                    x.impl_score_id === id ? { ...x, remarks: text } : x),
                                                            } : prev);
                                                        }}
                                                        onObservationsBlur={(id, text) => {
                                                            if (text && text.trim()) {
                                                                const r = validate(110, text);
                                                                if (!r.valid) {
                                                                    setObsErrors(prev => ({ ...prev, [id]: r.message ?? 'Invalid observations.' }));
                                                                    return;
                                                                }
                                                            }
                                                            setObsErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
                                                            const impl = impls.find(x => x.impl_score_id === id);
                                                            if (impl) updateImpl(impl, { remarks: text });
                                                        }}
                                                    />
                                                    {impls.some(i => obsErrors[i.impl_score_id]) && (
                                                        <div className="response-inline-warning" data-testid="MSG_ctp_obs_error">
                                                            {impls.map(i => obsErrors[i.impl_score_id]).find(Boolean)}
                                                        </div>
                                                    )}
                                                </>
                                            ) : undefined
                                        }
                                        inlineActions={
                                            <ResponseBar
                                                findings={{
                                                    label:
                                                        controlDefs.length > 0
                                                            ? `${controlDefs.length} deficienc${controlDefs.length === 1 ? 'y' : 'ies'} — view / raise`
                                                            : 'Raise a deficiency',
                                                    // Badge suppressed — list renders inline as text below (audit parity).
                                                    count: 0,
                                                    tone: 'danger',
                                                    onClick: () => { if (!writeBlocked) setRaiseFor(control); },
                                                }}
                                            />
                                        }
                                    />
                                );
                            })}
                        </SeamlessSurface>
                        </div>
                    </div>
                )}
                <Toast toast={toast} onClose={() => setToast(null)} />
            </Modal.Body>
            {!readOnly && (
                <Modal.Footer>
                    {!isChecker && (
                        <Button
                            variant="fill"
                            icon={Send}
                            onClick={handleSubmit}
                            disabled={submitting || !allDone || detail?.status === 'Complete' || detail?.status === 'Approved'}
                        >
                            {submitting
                                ? 'Submitting…'
                                : detail?.status === 'Complete' || detail?.status === 'Approved'
                                    ? 'Submitted'
                                    : 'Submit for Approval'}
                        </Button>
                    )}
                    {isChecker && (
                        <Button
                            variant="danger"
                            icon={X}
                            onClick={() => { setRejectReason(''); setRejectErr(null); setShowReject(true); }}
                            disabled={submitting || detail?.status !== 'Complete'}
                        >
                            {submitting ? 'Rejecting…' : 'Reject'}
                        </Button>
                    )}
                    {isChecker && (
                        <Button
                            variant="update"
                            icon={Check}
                            onClick={handleApprove}
                            disabled={submitting || detail?.status !== 'Complete'}
                        >
                            {submitting
                                ? 'Approving…'
                                : detail?.status === 'Approved'
                                    ? 'Approved'
                                    : 'Approve'}
                        </Button>
                    )}
                </Modal.Footer>
            )}

            {/* Checker reject-reason modal (replaces the native window.prompt). */}
            {showReject && (
                <Modal isOpen size="sm" onClose={() => setShowReject(false)}>
                    <Modal.Header onClose={() => setShowReject(false)}>Reject Assessment</Modal.Header>
                    <Modal.Body>
                        <p className="text-sm text-gray-500" style={{ marginBottom: 10 }}>
                            Send this assessment back to the maker. State why it is being rejected.
                        </p>
                        <FormControl.Textarea
                            rows={4}
                            value={rejectReason}
                            placeholder="Reason for rejection (required)"
                            error={rejectErr || undefined}
                            onChange={(e) => { setRejectReason(e.target.value); if (rejectErr) setRejectErr(null); }}
                            data-testid="INP_cat_program_reject_reason"
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline" onClick={() => setShowReject(false)} disabled={submitting} data-testid="BTN_cat_program_reject_cancel">Cancel</Button>
                        <Button variant="danger" icon={X} onClick={handleReject} disabled={submitting || !rejectReason.trim()} data-testid="BTN_cat_program_reject_confirm">
                            {submitting ? 'Rejecting…' : 'Reject'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {raiseFor && (
                <RaiseDeficiencyDialog
                    control={raiseFor}
                    programId={programId}
                    onClose={() => setRaiseFor(null)}
                    onCreated={(d) => {
                        setDeficiencies(prev => [d, ...prev]);
                        setRaiseFor(null);
                        setToast({ type: 'success', message: 'Deficiency raised.' });
                    }}
                />
            )}
            {remarks && (
                <RemarksModal
                    heading={remarks.heading}
                    title={remarks.title}
                    initialValue={remarks.value}
                    placeholder={remarks.placeholder}
                    testId={remarks.testId}
                    readOnly={readOnly}
                    onCommit={remarks.onCommit}
                    onClose={() => setRemarks(null)}
                />
            )}
        </Modal>
    );
};

interface RaiseDeficiencyDialogProps {
    control: ProgramControlRow;
    programId: number;
    onClose: () => void;
    onCreated: (d: ControlDeficiency) => void;
}

const RaiseDeficiencyDialog: React.FC<RaiseDeficiencyDialogProps> = ({ control, programId, onClose, onCreated }) => {
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState<DeficiencySeverity>('Medium');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const submit = async () => {
        if (!description.trim()) {
            setErr('Description is required');
            return;
        }
        const r = validate(110, description);
        if (!r.valid) {
            setErr(r.message ?? 'Invalid description.');
            return;
        }
        setSaving(true);
        try {
            const created = await controlDeficiencyService.create(control.control_id, {
                description: description.trim(),
                source: 'testing',
                source_ref_id: programId,
                detected_severity: severity,
            });
            onCreated(created);
        } catch (e: any) {
            setErr(extractApiError(e, 'Failed to raise deficiency'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen onClose={onClose} size="md">
            <Modal.Header>
                Raise Deficiency on "<span className="item-highlight">{control.control_code} — {control.control_name}</span>"
            </Modal.Header>
            <Modal.Body>
                <form
                    id="raise-deficiency-form"
                    onSubmit={e => { e.preventDefault(); submit(); }}
                    className="flex flex-col gap-y-4"
                >
                    <FormControl.SearchSelect
                        label="Severity"
                        required
                        value={severity}
                        onChange={(val) => setSeverity(val as DeficiencySeverity)}
                        options={DEFICIENCY_SEVERITIES.map(s => ({ value: s, label: s }))}
                    />
                    <FormControl.Textarea
                        label="Description"
                        required
                        rows={5}
                        value={description}
                        onChange={e => { setDescription(e.target.value); if (err) setErr(null); }}
                        onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                            const v = e.target.value;
                            if (v && v.trim()) { const r = validate(110, v); setErr(r.valid ? null : (r.message ?? 'Invalid description.')); }
                        }}
                        error={err || undefined}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    type="submit"
                    form="raise-deficiency-form"
                    variant="fill"
                    icon={Save}
                    disabled={saving || !description.trim()}
                >
                    {saving ? 'Saving…' : 'Submit'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TestProgramExecutionModal;
